import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import GraphHeader from "../../CommonComponents/GraphHeader";
import CommonLineGraph from "../../CommonComponents/CommonLineGraph";
import FecthingPage from "../../CommonComponents/FecthingPage";
import { OwnerAccountReport } from "../../../../../redux/features/Settings/Statistics";
import CommonToast from "../../../../_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";
import {
  errorcode,
  GlobalConfiguration,
  SettingsStatisticsConfiguration,
} from "../../../../../utils/configurationProvider";
import globalText from "../../../../../utils/globalText";
import IconIndicator from "../../../../_utils/IconIndicator";
import dayjs from "dayjs";
import { toCamelCase } from "../../../../../utils/toCamelCase";

/**
 * BalancePerDay - A component that displays balance trends over time
 * using a line graph. Shows daily balance amounts with totals. Supports filtering
 * by time period and amount unit.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.flow - Display flow: 'homeDashboard' or other
 * @param {boolean} [props.autoFetch=false] - Whether to fetch data automatically on mount
 * @param {Function} props.toggleAddedToHome - Callback to toggle widget on home dashboard
 * @param {boolean} props.isAddedToHome - Whether this widget is added to home dashboard
 * @returns {JSX.Element} Rendered balance graph component
 */
const BalancePerDay = ({
  flow,
  autoFetch = false,
  toggleAddedToHome,
  isAddedToHome,
}) => {
  /**
   * Default fallback data for line graph - used as initial state before API fetch.
   * @type {Object}
   * @property {number[]} defaultItemsSizeForXAxis - Array of indices for x-axis positions
   * @property {number[]} dataFirst - Balance amount line data
   * @property {string[]} xAxisData - Date labels for x-axis
   * @property {number} balanceAmount - Total balance amount
   */
  const DEFAULT_GRAPH_DATA = {
    // NOTE: This is default dummy data for graph - used as fallback before fetch
    defaultItemsSizeForXAxis: [0, 1, 2, 3, 4, 5, 6],
    dataFirst: [200, 500, 300, 100, 600, 800, 350],
    // dataSecond: [100, 700, 200, 300, 500, 400, 250],
    xAxisData: [
      "2025-01-01",
      "2025-01-02",
      "2025-01-03",
      "2025-01-04",
      "2025-01-05",
      "2025-01-06",
      "2025-01-07",
    ],
    totalOwnerAccount: GlobalConfiguration.lengthCheckZero,
    dayWiseEntries: [
      { date: "2025-01-01", amount: 200 },
      { date: "2025-01-02", amount: 500 },
      { date: "2025-01-03", amount: 300 },
      { date: "2025-01-04", amount: 100 },
      { date: "2025-01-05", amount: 600 },
      { date: "2025-01-06", amount: 800 },
      { date: "2025-01-07", amount: 350 },
    ],
    dayWiseTotalDebitAmount: 2850,
  };

  // Graph data state - initialized with default dummy data
  const [graphData, setGraphData] = useState(DEFAULT_GRAPH_DATA);
  const homeDashboard = toCamelCase(globalText?.HOME_DASHBOARD);

  // Time period filter state (defaults to last 7 days)
  const [selectedDays, setSelectedDays] = useState(
    globalText?.LAST_7_DAYS.toLowerCase(),
  );

  // Amount unit filter state (e.g., "", "thousand", "lakh", "crore")
  const [selectedAmount, setSelectedAmount] = useState("");

  // Controls whether the fetch page UI is shown (vs auto-fetched data)
  const [fetchPageEnabled, setFetchPageEnabled] = useState(!autoFetch);

  const [selectedAccount, setSelectedAccount] = useState("");

  // Loading state for manual fetch button
  const [fetchPageLoading, setFetchPagefetchPageLoading] = useState(false);

  // Error state for displaying error UI
  const [isError, setIsError] = useState(false);

  // Raw report data from API (stored for re-calculation when unit changes)
  const [rawReportData, setRawReportData] = useState(null);
  const toastShownRef = useRef(false);
  const services = useSelector((state) => state.refreshToken.services);
  const globalStatisticsService = services?.includes("VStatistics"); // Global service for statistics.

  const dispatch = useDispatch();
  const loginData = useSelector((state) => state.refreshToken.data);

  // Select loading and error states from Redux
  const { OwnerAccountReportLoading, OwnerAccountReportError } = useSelector(
    (state) => state.settingStatistics,
  );

  // Extract entity ID from login data if user is authenticated
  const entityId = loginData?.status ? loginData?.entity?.entity_id : "";

  /**
   * Transforms API report data into arrays suitable for dual-line graph rendering.
   * Handles amount formatting based on selected unit and extracts date labels.
   */
  const transformReportData = (reports, selectedAmount) => {
    return reports.reduce(
      (acc, val, index) => {
        // X-axis position index
        acc.defaultGraphDataItemsForXaxis.push(index);

        // Balance for each report date
        acc.graphDataFirst.push(
          selectedAmount
            ? formatTotalBalance(val.balance, selectedAmount).amount
            : val.balance,
        );

        acc.dayWiseEntries.push({ date: val.date, amount: val.balance });

        // Extract date label - prefers 'day' property, falls back to 'date'
        if (Object.hasOwn(val, globalText?.DAY.toLowerCase())) {
          acc.xAxisLabels.push(val.day);
        } else {
          acc.xAxisLabels.push(val.date);
        }

        return acc;
      },
      {
        defaultGraphDataItemsForXaxis: [],
        graphDataFirst: [],
        dayWiseEntries: [],
        xAxisLabels: [],
      },
    );
  };

  const formatDisplayedBalance = (balance) => {
    const formatted = formatTotalBalance(balance, selectedAmount || "amount");

    return `INR ${formatted.amount}${selectedAmount ? ` ${selectedAmount}` : ""}`;
  };

  /**
   * Processes API response and updates component state with credit/debit data.
   * On success: transforms data, shows success toast (once), updates graph data.
   * On failure: resets to default data, conditionally enables fetch page, sets error.
   */
  const handleCalculation = useCallback(
    (response) => {
      const reportData = response?.payload?.report_data;

      if (response?.payload?.status && reportData) {
        // Store raw data for re-calculation when amount unit changes
        setRawReportData(reportData);

        // Transform report data for graph
        const {
          defaultGraphDataItemsForXaxis,
          graphDataFirst,
          dayWiseEntries,
          xAxisLabels,
        } = transformReportData(
          reportData.generate_reports || [],
          selectedAmount,
        );

        // Update graph data with transformed arrays and summary totals
        setGraphData({
          defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
          dataFirst: graphDataFirst,
          xAxisData: xAxisLabels,
          dayWiseEntries,
          dayWiseTotalDebitAmount: reportData.total_balance,
          totalOwnerAccount: reportData.total_owner_account ?? 0,
        });
        // toast shows only once
        if (!toastShownRef.current && fetchPageEnabled) {
          CommonToast({
            text: "Fetched successfully.",
            icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
            variant: "var(--success)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          toastShownRef.current = true;
        }
        setFetchPageEnabled(false);
      } else {
        // fallback to defaults
        setGraphData(DEFAULT_GRAPH_DATA);
        // Enable fetch page unless it's a "not found" error (no data exists)
        setFetchPageEnabled(
          response?.payload?.status !== errorcode?.errorNotFound,
        );
        toastShownRef.current = false;
        setIsError(true);
      }
    },
    [selectedDays, entityId],
  );

  /**
   * Fetches credit/debit amount data from API and updates component state.
   * Requires valid entity ID.
   */
  const fetchData = async () => {
    // Guard until both the entity and owner account selection are available.
    if (!entityId || !selectedAccount) return;
    setIsError(false);

    const response = await dispatch(
      OwnerAccountReport({
        entity_id: entityId,
        period: selectedDays,
        owner_account_ids: selectedAccount,
      }),
    );
    setFetchPagefetchPageLoading(false);
    handleCalculation(response);
  };

  /**
   * Handler for manual "Fetch Data" button click.
   * Initiates fetch by enabling loading and disabling fetch page.
   */
  const handleFetchClick = () => {
    setFetchPagefetchPageLoading(true);
    setFetchPageEnabled(false);
  };

  /**
   * Effect: Trigger data fetch when autoFetch is enabled or fetch page is disabled.
   * Re-runs when autoFetch, fetch state, period, or owner account selection changes.
   */
  useEffect(() => {
    if (!fetchPageEnabled || autoFetch) {
      fetchData();
    }
  }, [autoFetch, fetchPageEnabled, selectedDays, selectedAccount]);

  /**
   * Effect: Recalculate graph data when only the amount unit changes.
   * Uses cached rawReportData to avoid re-fetching from API.
   * Re-runs when selectedAmount or rawReportData changes.
   */
  useEffect(() => {
    if (rawReportData) {
      const {
        defaultGraphDataItemsForXaxis,
        graphDataFirst,
        dayWiseEntries,
        xAxisLabels,
      } = transformReportData(
        rawReportData.generate_reports || [],
        selectedAmount,
      );

      setGraphData({
        defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
        dataFirst: graphDataFirst,
        xAxisData: xAxisLabels,
        dayWiseEntries,
        dayWiseTotalDebitAmount: rawReportData.total_balance,
        totalOwnerAccount: rawReportData.total_owner_account ?? 0,
      });
    }
  }, [selectedAmount, rawReportData]);

  return (
    <div
      className={`w-width-full flex flex-col bg-primary-card ${flow === "homeDashboard" ? "rounded-b-border-radius-md" : "rounded-border-radius-md"} Qsmd:max-h-[355px]`}
    >
      {/* Header with title, period filter, amount unit filter, and add-to-home toggle */}
      <GraphHeader
        handlingOptionsRequired={[
          {
            type: `${globalText?.DAY.toLowerCase()}s`,
            setterState: setSelectedDays,
          },
          { type: "allAccounts", setterState: setSelectedAccount },
          {
            type: `${globalText?.AMOUNT.toLowerCase()}s`,
            setterState: setSelectedAmount,
          },
        ]}
        flow={flow}
        autoFetch={autoFetch}
        titleText={globalText?.BALANCE_PER_DAY}
        hideTitle={flow === homeDashboard ? true : false}
        isAddedToHome={isAddedToHome}
        toggleAddedToHome={toggleAddedToHome}
      />
      <div className="flex Qsmd:flex-row flex-col-reverse relative overflow-hidden">
        {/* Fetch/Loading/Error state page */}
        <FecthingPage
          fetchPageEnabled={
            (!autoFetch && fetchPageEnabled) || !globalStatisticsService
          }
          loading={OwnerAccountReportLoading || fetchPageLoading}
          isError={isError}
          errorData={
            OwnerAccountReportError === errorcode?.errorNotFound
              ? { status: errorcode?.errorNotFound }
              : null
          }
          handleFunction={handleFetchClick}
        />

        <div className="Qsmd:max-w-column-width-md Qsmd:w-width-sm w-full h-auto flex flex-col bg-primary-nav items-center flex-shrink-0 Qsmd:min-h-0 Qsmd:rounded-b-none Qsmd:rounded-bl-border-radius-md rounded-b-border-radius-md">
          <div className="w-width-full">
            <div className="text-text-secondary text-font-xxs font-semibold mb-margin-lg"></div>
            <div className="flex flex-col gap-gap-sm w-width-full h-height-full Qsmd:flex-1 Qsmd:min-h-0 Qsmd:overflow-y-auto Qsmd:pb-padding-md pb-padding-sm px-padding-md Qsmd:max-h-[var(--statistics-container-height)] max-h-[190px] overflow-y-auto">
              {graphData?.dayWiseEntries?.map((val, index) => {
                return (
                  <div
                    className="flex justify-between"
                    key={`${val?.date}-${index}`}
                  >
                    <div className="text-text-secondary font-normal Qsmd:text-font-xxs text-font-xs">
                      {/* Format date as "DD MMM" (e.g., "01 Jan") */}
                      {dayjs(val?.date).format("DD MMM")}
                    </div>
                    <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-semibold">
                      {fetchPageEnabled
                        ? "-"
                        : formatDisplayedBalance(val?.amount)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Day-wise Total Row */}
          <div className="w-width-full h-auto justify-between mt-margin-sm mb-margin-sm px-padding-md Qsmd:my-margin-base Qsmd:pt-padding-md Qsmd:pb-padding-lg">
            <div className="border-b-border border-primary-bg w-full"></div>
            <div className="flex flex-shrink-0 justify-between mt-margin-md">
              <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs">
                Total
              </div>
              <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-semibold">
                {fetchPageEnabled
                  ? "-"
                  : formatDisplayedBalance(graphData?.dayWiseTotalDebitAmount)}
              </div>
            </div>
          </div>
          <div className="w-width-full bg-primary-nav rounded-br-border-radius-md Qsmd:rounded-bl-none rounded-bl-border-radius-md Qsmd:hidden block">
            <div className="flex Qsmd:flex-row flex-col gap-gap-sm py-padding-sm px-padding-md">
              {/* Credit Amount Card */}
              <div className="flex items-center Qsmd:flex-1 gap-gap-xs ">
                <div className="text-text-secondary text-font-xxs">
                  <div className="flex items-center">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      className="flex-shrink-0 pointer-events-none"
                    >
                      <circle
                        cx={GlobalConfiguration?.lengthCheckSeven}
                        cy={GlobalConfiguration?.lengthCheckSeven}
                        r={
                          SettingsStatisticsConfiguration?.outerStrokeRingRadius
                        }
                        fill="none"
                        stroke="var(--settled)"
                        strokeWidth={
                          SettingsStatisticsConfiguration?.outerStrokeWidth
                        }
                        style={{ pointerEvents: "none" }}
                      />
                      <circle
                        cx={GlobalConfiguration?.lengthCheckSeven}
                        cy={GlobalConfiguration?.lengthCheckSeven}
                        r={
                          SettingsStatisticsConfiguration?.innerSolidCircleRadius
                        }
                        fill="var(--settled)"
                        stroke="none"
                        style={{ pointerEvents: "none" }}
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-normal">
                  : {globalText?.BALANCE_PER_DAY}
                </div>
              </div>

              {/* Debit Amount Card - bordered separator on desktop */}
              <div className="flex Qsmd:flex-1 gap-gap-sm items-center Qsmd:border-l-border Qsmd:border-t-border-base Qsmd:pl-padding-md border-t-border Qsmd:border-l-primary-bg border-t-primary-bg Qsmd:pt-0 pt-padding-sm ">
                <div className="text-text-secondary text-font-xxs">
                  Total owners account :
                </div>
                <div className="text-text-secondary text-font-xs font-semibold">
                  {fetchPageEnabled || isError
                    ? "-"
                    : graphData.totalOwnerAccount}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Line Graph Container with horizontal scrolling for many data points */}
        <div className="w-full overflow-x-hidden">
          <div className="w-width-full relative overflow-x-auto pb-padding-xs">
            {/* Dynamic minimum width based on data points count for proper spacing */}
            <div
              style={{
                minWidth: `${Math.max((graphData.xAxisData?.length || GlobalConfiguration?.lengthCheckZero) * SettingsStatisticsConfiguration?.graphMinWidthPerDataPoint)}px`,
                paddingBottom: "var(--padding-xs)",
              }}
            >
              <CommonLineGraph
                graphDataFirst={graphData.dataFirst}
                xAxisLabels={graphData.xAxisData}
                graphDataFirstColor={"var(--settled)"}
                defaultDataItemsForXaxis={graphData.defaultItemsSizeForXAxis}
                amountFormatForLegend={selectedAmount}
                normalizePositiveAndNegativeValues
                period={selectedDays}
              />
            </div>
          </div>
          {/* Footer with Credit/Debit totals */}

          <div className="w-width-full bg-primary-nav rounded-br-border-radius-md Qsmd:rounded-bl-none rounded-bl-border-radius-md Qsmd:block hidden">
            <div className="flex Qsmd:flex-row flex-col gap-gap-sm py-padding-sm px-padding-md">
              {/* Credit Amount Card */}
              <div className="flex items-center Qsmd:flex-1 gap-gap-xs ">
                <div className="text-text-secondary text-font-xxs">
                  <div className="flex items-center">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      className="flex-shrink-0 pointer-events-none"
                    >
                      <circle
                        cx={GlobalConfiguration?.lengthCheckSeven}
                        cy={GlobalConfiguration?.lengthCheckSeven}
                        r={
                          SettingsStatisticsConfiguration?.outerStrokeRingRadius
                        }
                        fill="none"
                        stroke="var(--settled)"
                        strokeWidth={
                          SettingsStatisticsConfiguration?.outerStrokeWidth
                        }
                        style={{ pointerEvents: "none" }}
                      />
                      <circle
                        cx={GlobalConfiguration?.lengthCheckSeven}
                        cy={GlobalConfiguration?.lengthCheckSeven}
                        r={
                          SettingsStatisticsConfiguration?.innerSolidCircleRadius
                        }
                        fill="var(--settled)"
                        stroke="none"
                        style={{ pointerEvents: "none" }}
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-normal">
                  : {globalText?.BALANCE_PER_DAY}
                </div>
              </div>

              {/* Debit Amount Card - bordered separator on desktop */}
              <div className="flex Qsmd:flex-1 gap-gap-sm items-center Qsmd:border-l-border Qsmd:border-t-border-base Qsmd:pl-padding-md border-t-border Qsmd:border-l-primary-bg border-t-primary-bg Qsmd:pt-0 pt-padding-sm ">
                <div className="text-text-secondary text-font-xxs">
                  Total owners account :
                </div>
                <div className="text-text-secondary text-font-xs font-semibold">
                  {fetchPageEnabled || isError
                    ? "-"
                    : graphData.totalOwnerAccount}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalancePerDay;
